import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { trailMaxAge, useStore } from '../state/store';
import { useLiveTraffic } from '../hooks/useLiveTraffic';
import {
  bboxFromString,
  type Aircraft,
  type Airport,
  type ApproachProcedure,
  type Bbox,
  type CoverageCircle,
  type RouteEndpoints,
} from '../types';
import {
  AIRPORTS_LAYER,
  PLANES_LAYER,
  applyHobbyistVisibility,
  setupLayers,
  updateAirports,
  updateAllRoutes,
  updateApproaches,
  updateCoverage,
  updateCoverageCircle,
  updateFlightLanes,
  updateFocusTrack,
  updateGlideSlope3D,
  updatePlanes,
  updatePredicted,
  updateRings,
  updateRoute,
  updateTrails,
  projectPosition,
} from '../map/layers';
import { distanceKm } from '../map/geo';
import { accumulate, createDensityGrid, prune, type DensityGrid } from '../map/density';
import { addRadarLayers, updateRadarCoverage } from '../map/radarCoverage';
import { addSatelliteLayers, loadTles, updateSatellites } from '../map/satellites';
import { addWeatherLayers, refreshMetars, refreshPrecip, setMetarsVisible, setPrecipVisible } from '../map/weather';
import { mergeApproaches } from '../data/approaches';
import { matchesFilters } from '../map/filters';
import FlightInfoPanel from '../components/FlightInfoPanel';
import AirportPanel from '../components/AirportPanel';
import AircraftList from '../components/AircraftList';
import StatusBar from '../components/StatusBar';
import SearchBox from '../components/SearchBox';
import FilterPanel from '../components/FilterPanel';
import MapToolbar from '../components/MapToolbar';
import WatchlistPanel from '../components/WatchlistPanel';
import ToastStack from '../components/ToastStack';
import AltitudeLegend from '../components/AltitudeLegend';
import ProjectorOverlay, { useProjectorMode } from '../projector/ProjectorMode';
import { useFullscreen } from '../hooks/useFullscreen';
import { isPhone, useIsPhone } from '../hooks/useIsPhone';
import InstallPrompt from '../components/InstallPrompt';
import LocationHelp from '../components/LocationHelp';
import { useLocalNetworkHint } from '../localNetwork';
import { useAlertEngine } from '../alerts';
import { navigate } from '../navigate';
import { AirplaneTilt, ArrowLeft, CircleDashed, CornersIn, CornersOut, NavigationArrow, Television, WifiSlash } from '@phosphor-icons/react';
import { useLocateMe } from '../geolocate';
import { apiUrl } from '../api';

const LIBERTY_STYLE = 'https://tiles.openfreemap.org/styles/liberty';
const DARK_STYLE = 'https://tiles.openfreemap.org/styles/dark';

/** Raster OSM fallback style if the vector style fails to load. */
const RASTER_FALLBACK: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
  glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
};

/** Bounding box of a coverage circle: same flat-earth deg/km approximation the moveend follow-view logic below uses (MAX_HALF_LAT), clamped to ±0.2 cos so a circle centered near a pole doesn't blow up the longitude half-width. */
function circleToBbox(c: CoverageCircle): Bbox {
  const latHalf = c.radiusKm / 111.32;
  const lonHalf = c.radiusKm / (111.32 * Math.max(0.2, Math.cos((c.lat * Math.PI) / 180)));
  return {
    lamin: Math.max(-90, c.lat - latHalf),
    lamax: Math.min(90, c.lat + latHalf),
    lomin: Math.max(-180, c.lon - lonHalf),
    lomax: Math.min(180, c.lon + lonHalf),
  };
}


export default function MapView() {
  const params = useMemo(() => new URLSearchParams(location.search), []);
  const bbox = useMemo(() => bboxFromString(params.get('bbox')), [params]);
  const areaName = params.get('name');
  const locateMe = useLocateMe();
  const { projector, toggleProjector } = useProjectorMode();
  const { isFullscreen, toggleFullscreen } = useFullscreen();

  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  // Latest bulk route-endpoint lookup, shared between the polling effect and
  // the per-frame tick loop that draws the predicted remaining-leg lines.
  const routesRef = useRef<Record<string, RouteEndpoints>>({});
  // Latest merged approach-procedure list, kept alongside the map source
  // pushed by `updateApproaches` so the per-tick loop can re-raise the 3D
  // glide-slope ramp (`updateGlideSlope3D`) off the live camera pitch
  // without re-fetching on every frame.
  const approachesRef = useRef<ApproachProcedure[]>([]);
  // Observed flight-lane density grid: persists across map re-creation
  // (e.g. projector-mode toggles) so the accumulated picture isn't lost.
  const laneGridRef = useRef<DensityGrid>(createDensityGrid());
  // Cache of currently visible (post-filter) aircraft, recomputed only when
  // the store's aircraft/filters actually change rather than every frame.
  const visibleRef = useRef<Aircraft[]>([]);
  const [mapReady, setMapReady] = useState(false);
  const userLocation = useStore((s) => s.userLocation);
  const setArea = useStore((s) => s.setArea);
  const setAirports = useStore((s) => s.setAirports);
  const isEmpty = useStore((s) => s.lastUpdateTs !== null && s.aircraft.size === 0);
  const isWaiting = useStore((s) => s.lastUpdateTs === null && s.aircraft.size === 0);
  const networkHint = useLocalNetworkHint(isWaiting);
  const allSourcesDown = useStore((s) => s.sources.length > 0 && s.sources.every((x) => !x.ok));
  const threeD = useStore((s) => s.layers.threeD);
  const coverageCircle = useStore((s) => s.coverageCircle);
  const drawingCircle = useStore((s) => s.drawingCircle);
  const hobbyist = useStore((s) => s.layers.hobbyist);
  const trails = useStore((s) => s.trails);
  // Live-data coverage: the originally picked area by default, or the current
  // map viewport (updated on pan/zoom) when "follow view" is enabled.
  const [liveBbox, setLiveBbox] = useState<Bbox | null>(bbox);

  useLiveTraffic(liveBbox, trails === 'full');
  useAlertEngine();

  const trueScale = useStore((s) => s.layers.trueScale);
  useEffect(() => {
    try {
      localStorage.setItem('planesight:trueScale', trueScale ? '1' : '0');
    } catch {
      /* private mode */
    }
  }, [trueScale]);

  // The user's shared location, as a pulsing dot.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !userLocation) return;
    const el = document.createElement('div');
    el.className = 'user-dot';
    el.title = 'Your location';
    const marker = new maplibregl.Marker({ element: el }).setLngLat([userLocation.lon, userLocation.lat]).addTo(map);
    return () => {
      marker.remove();
    };
  }, [mapReady, userLocation]);

  // Persist the hobbyist toggle across browser sessions. The initial value
  // is already restored at zustand-store init (see `initialHobbyist` in
  // state/store.ts), so no mount-time restore effect is needed.
  useEffect(() => {
    try {
      localStorage.setItem('planesight:hobbyist', hobbyist ? '1' : '0');
    } catch {
      /* private mode */
    }
  }, [hobbyist]);

  useEffect(() => {
    if (bbox) setArea(bbox, areaName);
    setLiveBbox(bbox);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbox && `${bbox.lamin},${bbox.lomin},${bbox.lamax},${bbox.lomax}`, areaName, setArea]);

  // A coverage circle (Layers menu "Coverage circle" tool) pins live coverage
  // to its bounding box; clearing it snaps live coverage back to the
  // originally picked area, same as leaving "follow view" used to.
  useEffect(() => {
    setLiveBbox(coverageCircle ? circleToBbox(coverageCircle) : bbox);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverageCircle]);

  // Shareable deep link to one flight: `?sel=<hex>` selects it (once it shows
  // up in the live feed — it may take a poll cycle or two), and selecting a
  // flight rewrites the URL so the address bar is always copy-pasteable.
  // replaceState, not pushState: selecting planes shouldn't stack history.
  const selectedIcao24 = useStore((s) => s.selectedIcao24);
  const phone = useIsPhone();
  const wantedSel = params.get('sel');
  const selAppliedRef = useRef(false);
  useEffect(() => {
    if (selAppliedRef.current || !wantedSel) return;
    const unsub = useStore.subscribe((s) => {
      if (selAppliedRef.current) return;
      if (s.aircraft.has(wantedSel.toLowerCase())) {
        selAppliedRef.current = true;
        s.select(wantedSel.toLowerCase());
      }
    });
    return unsub;
  }, [wantedSel]);

  useEffect(() => {
    const url = new URL(location.href);
    if (selectedIcao24) url.searchParams.set('sel', selectedIcao24);
    else url.searchParams.delete('sel');
    if (url.href !== location.href) history.replaceState(null, '', url);
  }, [selectedIcao24]);

  // Create the map (recreated when projector mode flips: different style/theme).
  useEffect(() => {
    if (!mapContainer.current || !bbox) return;
    setMapReady(false);
    const map = new maplibregl.Map({
      container: mapContainer.current,
      // Hobbyist mode forces the dark base style regardless of projector.
      style: projector || hobbyist ? DARK_STYLE : LIBERTY_STYLE,
      bounds: [
        [bbox.lomin, bbox.lamin],
        [bbox.lomax, bbox.lamax],
      ],
      fitBoundsOptions: { padding: 20 },
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    // Phones: start with the attribution collapsed to its (i) button; expanded
    // it runs under the aircraft-list button. Still one tap away.
    if (isPhone()) {
      map.once('load', () => {
        map.getContainer().querySelector('.maplibregl-ctrl-attrib')?.classList.remove('maplibregl-compact-show');
      });
    }
    if (!projector) {
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    }

    let fellBack = false;
    map.on('error', (e) => {
      const msg = String(e.error?.message ?? '');
      if (!fellBack && !map.isStyleLoaded() && /style|Failed to fetch|HTTP/i.test(msg)) {
        fellBack = true;
        map.setStyle(RASTER_FALLBACK);
      }
    });

    const onLoad = () => {
      setupLayers(map, { projector, hobbyist });
      addRadarLayers(map, { projector });
      addSatelliteLayers(map, { projector });
      addWeatherLayers(map, { projector });
      // Strip streets/buildings/POIs and seed the approach overlays for the
      // hobbyist view with the curated (real-ILS) set — the bbox-scoped
      // fetch effect below fills in the runway-derived worldwide set once
      // liveBbox is known and merges it in. Both are no-op'd visually here
      // if hobbyist is off — `setupLayers` already adds the layers with
      // visibility=none.
      applyHobbyistVisibility(map, hobbyist);
      approachesRef.current = mergeApproaches([]);
      updateApproaches(map, approachesRef.current);
      setMapReady(true);
    };
    map.on('load', onLoad);
    map.on('styledata', () => {
      if (fellBack && map.isStyleLoaded()) onLoad();
    });

    map.on('click', PLANES_LAYER, (e) => {
      const f = e.features?.[0];
      if (f?.properties?.icao24) {
        useStore.getState().select(f.properties.icao24 as string);
      }
    });
    // Clicking an airport opens its arrivals/departures board.
    map.on('click', AIRPORTS_LAYER, (e) => {
      const ident = e.features?.[0]?.properties?.ident;
      if (typeof ident === 'string' && ident) useStore.getState().selectAirport(ident);
    });
    map.on('click', (e) => {
      // Clicking empty map clears both panels; a hit on either interactive
      // layer is handled by its own click handler above.
      const layers = [PLANES_LAYER, AIRPORTS_LAYER].filter((l) => map.getLayer(l));
      const hits = map.queryRenderedFeatures(e.point, { layers });
      if (hits.length === 0 && map.getLayer(PLANES_LAYER)) {
        // True-size planes can be a few pixels across when zoomed out: a tap
        // within a fingertip of one selects the nearest.
        const r = 14;
        const near = map.queryRenderedFeatures(
          [
            [e.point.x - r, e.point.y - r],
            [e.point.x + r, e.point.y + r],
          ],
          { layers: [PLANES_LAYER] },
        );
        let best: { icao24: string; d: number } | null = null;
        for (const f of near) {
          const icao24 = f.properties?.icao24 as string | undefined;
          if (!icao24 || f.geometry.type !== 'Point') continue;
          const p = map.project(f.geometry.coordinates as [number, number]);
          const d = Math.hypot(p.x - e.point.x, p.y - e.point.y);
          if (!best || d < best.d) best = { icao24, d };
        }
        if (best) {
          useStore.getState().select(best.icao24);
          return;
        }
      }
      if (hits.length === 0) {
        useStore.getState().select(null);
        useStore.getState().selectAirport(null);
      }
    });
    map.on('mouseenter', PLANES_LAYER, () => (map.getCanvas().style.cursor = 'pointer'));
    map.on('mouseleave', PLANES_LAYER, () => (map.getCanvas().style.cursor = ''));
    map.on('mouseenter', AIRPORTS_LAYER, () => (map.getCanvas().style.cursor = 'pointer'));
    map.on('mouseleave', AIRPORTS_LAYER, () => (map.getCanvas().style.cursor = ''));
    // Turning the map by hand cancels follow mode.
    map.on('dragstart', () => {
      if (useStore.getState().followSelected) useStore.getState().setFollow(false);
    });

    // "Follow view": when enabled, expand live coverage to whatever's
    // currently visible instead of just the originally picked area.
    let moveDebounce: number | undefined;
    // Bbox actually subscribed to (padded, see below) and the zoom at which
    // it was picked. Live only for this map instance — recreated whenever
    // the map itself is (hobbyist/projector toggle), which is fine since a
    // fresh map has no subscription to be hysteretic about yet.
    let subscribedBbox: Bbox | null = null;
    let subscribedZoom: number | null = null;
    map.on('moveend', () => {
      // A coverage circle pins live coverage to a fixed area — bypass
      // follow-view entirely while one exists (see circleToBbox effect above).
      if (useStore.getState().coverageCircle) return;
      window.clearTimeout(moveDebounce);
      moveDebounce = window.setTimeout(() => {
        const b = map.getBounds();
        const c = map.getCenter();
        const zoom = map.getZoom();
        // adsb.fi/adsb.lol cap out at a 250 nm radius per request (a
        // real API limit, not a choice made here) regardless of bbox size, so
        // past that point they only ever cover a small circle near the query
        // center — OpenSky is the only one of the three that can actually
        // answer an arbitrarily large or whole-world bbox in one request.
        // Zoomed out far enough to see most of the globe: request the whole
        // world in one shot rather than continuing to grow the box, so
        // there's one canonical "world" watcher instead of a slightly
        // different giant bbox (and thus a new cold-start watcher) on every
        // tiny pan at this zoom level.
        const WORLD_VIEW_THRESHOLD_DEG = 60; // viewport taller than this ~ treat as world view
        if (b.getNorth() - b.getSouth() >= WORLD_VIEW_THRESHOLD_DEG) {
          if (subscribedBbox?.lamin !== -90 || subscribedBbox?.lomin !== -180) {
            subscribedBbox = { lamin: -90, lomin: -180, lamax: 90, lomax: 180 };
            subscribedZoom = zoom;
            setLiveBbox(subscribedBbox);
          }
          return;
        }
        // Otherwise scale the live query to the actual viewport, capped at a
        // generous ceiling — big enough to comfortably cover a whole country
        // or continent (OpenSky handles this fine; the point/radius sources
        // just contribute less the further you get from their 250 nm circle,
        // which is an honest limitation of those free feeds, not a bug).
        const MAX_HALF_LAT = 25; // deg (~1500 nm)
        const maxHalfLon = MAX_HALF_LAT / Math.max(0.2, Math.cos((c.lat * Math.PI) / 180));
        const lamin = Math.max(-90, Math.max(b.getSouth(), c.lat - MAX_HALF_LAT));
        const lamax = Math.min(90, Math.min(b.getNorth(), c.lat + MAX_HALF_LAT));
        const lomin = Math.max(-180, Math.max(b.getWest(), c.lng - maxHalfLon));
        const lomax = Math.min(180, Math.min(b.getEast(), c.lng + maxHalfLon));
        if (lamin >= lamax || lomin >= lomax) return;
        const viewBbox: Bbox = { lamin, lomin, lamax, lomax };

        // Hysteresis: only resubscribe when the real viewport escapes the
        // padded area already subscribed, or zoom changed materially (a full
        // level+) — kills the resubscribe storm from follow-view panning and
        // small oscillation, which otherwise cold-starts a new watcher (or
        // at least a new subscribe round-trip) on nearly every moveend.
        const escapesSubscribed =
          !subscribedBbox ||
          viewBbox.lamin < subscribedBbox.lamin ||
          viewBbox.lamax > subscribedBbox.lamax ||
          viewBbox.lomin < subscribedBbox.lomin ||
          viewBbox.lomax > subscribedBbox.lomax;
        const zoomChanged = subscribedZoom === null || Math.abs(zoom - subscribedZoom) >= 1;
        if (!escapesSubscribed && !zoomChanged) return;

        // Pad ~1.3x around the viewport so small pans/zoom wobble stay
        // inside the already-subscribed area instead of re-triggering.
        const PAD = 1.3;
        const latPad = ((lamax - lamin) * (PAD - 1)) / 2;
        const lonPad = ((lomax - lomin) * (PAD - 1)) / 2;
        const padded: Bbox = {
          lamin: Math.max(-90, lamin - latPad),
          lomin: Math.max(-180, lomin - lonPad),
          lamax: Math.min(90, lamax + latPad),
          lomax: Math.min(180, lomax + lonPad),
        };
        subscribedBbox = padded;
        subscribedZoom = zoom;
        setLiveBbox(padded);
      }, 400);
    });

    return () => {
      window.clearTimeout(moveDebounce);
      mapRef.current = null;
      map.remove();
    };
  }, [bbox && `${bbox.lamin},${bbox.lomin}`, projector, hobbyist]); // eslint-disable-line react-hooks/exhaustive-deps

  // Airports for the currently live-covered area (follows liveBbox so panned-into
  // areas get their airport markers too).
  useEffect(() => {
    if (!liveBbox) return;
    const q = new URLSearchParams({
      lamin: String(liveBbox.lamin),
      lomin: String(liveBbox.lomin),
      lamax: String(liveBbox.lamax),
      lomax: String(liveBbox.lomax),
    });
    fetch(apiUrl(`/api/airports?${q}`))
      .then((r) => r.json())
      .then((d: { airports: Airport[] }) => setAirports(d.airports ?? []))
      .catch(() => setAirports([]));
  }, [liveBbox, setAirports]);

  // Runway-derived approach overlays (hobbyist "every airport worldwide"
  // glide-slope view) for the currently live-covered area, merged with the
  // curated real-ILS set. Mirrors the airports fetch above, but pushes
  // straight into the map (there's no store slice for this, same as the
  // rest of the map-source updates) — needs mapReady since, unlike airports,
  // there's no separate store-driven sync effect to re-apply it later.
  useEffect(() => {
    if (!liveBbox || !mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    const q = new URLSearchParams({
      lamin: String(liveBbox.lamin),
      lomin: String(liveBbox.lomin),
      lamax: String(liveBbox.lamax),
      lomax: String(liveBbox.lomax),
    });
    let cancelled = false;
    fetch(apiUrl(`/api/approaches?${q}`))
      .then((r) => r.json())
      .then((d: { approaches: ApproachProcedure[] }) => {
        if (cancelled || !mapRef.current) return;
        approachesRef.current = mergeApproaches(d.approaches ?? []);
        updateApproaches(map, approachesRef.current);
      })
      .catch(() => {
        if (cancelled || !mapRef.current) return;
        approachesRef.current = mergeApproaches([]);
        updateApproaches(map, approachesRef.current);
      });
    return () => {
      cancelled = true;
    };
  }, [liveBbox, mapReady]);

  // Live data coverage boundary: the literal bbox currently being requested
  // from the server (liveBbox — the same value passed to useLiveTraffic),
  // not a fixed center like the rings. liveBbox isn't in the zustand store
  // (it's local React state, see above), so this needs its own effect rather
  // than living in the store.subscribe draw effect below.
  const coverageOn = useStore((s) => s.layers.coverage);
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    updateCoverage(map, coverageOn ? liveBbox : null);
  }, [mapReady, coverageOn, liveBbox]);

  // Re-render the committed coverage circle whenever it changes, and also
  // after map recreation (hobbyist/projector toggle re-creates the whole map
  // instance, wiping every source) — mirrors the coverage-boundary effect
  // above. Skipped while actively drawing so it doesn't clobber the live
  // drag preview drawn by the effect below.
  useEffect(() => {
    if (!mapReady || drawingCircle) return;
    const map = mapRef.current;
    if (!map) return;
    updateCoverageCircle(map, coverageCircle);
  }, [mapReady, coverageCircle, drawingCircle]);

  // Coverage-circle draw tool: armed via the Layers menu ("Coverage circle"),
  // this wires mousedown (drop center) -> mousemove (grow radius, live
  // preview) -> mouseup (commit) directly on the map, same touch events too
  // (MapLibre fires touchstart/touchmove/touchend with `lngLat` the same way
  // as their mouse equivalents). Keyed on [mapReady, drawingCircle] so it's
  // re-wired against the current map instance whenever either changes,
  // including after a hobbyist/projector map recreation.
  useEffect(() => {
    if (!mapReady || !drawingCircle) return;
    const map = mapRef.current;
    if (!map) return;

    map.dragPan.disable();
    map.doubleClickZoom.disable();
    map.getCanvas().style.cursor = 'crosshair';

    let center: [number, number] | null = null;
    let dragging = false;

    const onDown = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
      dragging = true;
      center = [e.lngLat.lng, e.lngLat.lat];
      updateCoverageCircle(map, { lat: center[1], lon: center[0], radiusKm: 0 });
    };
    const onMove = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
      if (!dragging || !center) return;
      const radiusKm = distanceKm(center, [e.lngLat.lng, e.lngLat.lat]);
      updateCoverageCircle(map, { lat: center[1], lon: center[0], radiusKm });
    };
    const onUp = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
      if (!dragging || !center) return;
      dragging = false;
      const radiusKm = distanceKm(center, [e.lngLat.lng, e.lngLat.lat]);
      const [lon, lat] = center;
      center = null;
      // Accidental click (no real drag) — cancel instead of committing a
      // degenerate near-zero-radius circle.
      if (radiusKm < 1) {
        useStore.getState().setDrawingCircle(false);
        return;
      }
      useStore.getState().setCoverageCircle({ lat, lon, radiusKm });
      useStore.getState().setDrawingCircle(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') useStore.getState().setDrawingCircle(false);
    };

    map.on('mousedown', onDown);
    map.on('touchstart', onDown);
    map.on('mousemove', onMove);
    map.on('touchmove', onMove);
    map.on('mouseup', onUp);
    map.on('touchend', onUp);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      // Same stale-map race as the tick effect: a hobbyist/projector toggle
      // mid-draw removes this map before mapReady flips — don't poke handlers
      // on the dead instance (the new map starts with them enabled anyway).
      if (mapRef.current !== map) {
        document.removeEventListener('keydown', onKeyDown);
        return;
      }
      map.dragPan.enable();
      map.doubleClickZoom.enable();
      map.getCanvas().style.cursor = '';
      map.off('mousedown', onDown);
      map.off('touchstart', onDown);
      map.off('mousemove', onMove);
      map.off('touchmove', onMove);
      map.off('mouseup', onUp);
      map.off('touchend', onUp);
      document.removeEventListener('keydown', onKeyDown);
      // Cancelling mid-draw (Escape / re-clicking the toolbar item) leaves a
      // half-drawn preview on the map — restore whatever was actually
      // committed (or clear it if nothing was).
      updateCoverageCircle(map, useStore.getState().coverageCircle);
    };
  }, [mapReady, drawingCircle]);

  // Push store changes into map sources; dead-reckon plane motion between updates.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    const recomputeVisible = () => {
      const s = useStore.getState();
      visibleRef.current = [...s.aircraft.values()].filter((ac) => matchesFilters(ac, s.filters));
    };

    // Aircraft that just dropped out of the visible set (landed and filtered
    // out via "hide on ground", or genuinely lost) keep their trail on the
    // map for a moment, fading out, instead of it vanishing on the next
    // redraw. Plain closure state (not useRef) since only this effect reads
    // it, same pattern as `lastPlanes`/`raf` below.
    const FADE_DURATION_MS = 1500;
    let prevVisible = new Map<string, Aircraft>();
    const fading = new Map<string, { ac: Aircraft; fadeStart: number }>();

    const trackDisappearances = (list: Aircraft[]) => {
      const now = Date.now();
      const currentIds = new Set(list.map((a) => a.icao24));
      for (const [icao24, ac] of prevVisible) {
        if (!currentIds.has(icao24) && !fading.has(icao24)) {
          fading.set(icao24, { ac, fadeStart: now });
        }
      }
      prevVisible = new Map(list.map((a) => [a.icao24, a]));
    };

    const fadingList = (): { ac: Aircraft; fadeOpacity: number }[] => {
      const now = Date.now();
      const out: { ac: Aircraft; fadeOpacity: number }[] = [];
      for (const [icao24, entry] of fading) {
        const fadeOpacity = 1 - (now - entry.fadeStart) / FADE_DURATION_MS;
        if (fadeOpacity <= 0) {
          fading.delete(icao24);
          continue;
        }
        out.push({ ac: entry.ac, fadeOpacity });
      }
      return out;
    };

    let raf = 0;
    let lastPlanes = 0;
    const tick = (t: number) => {
      raf = requestAnimationFrame(tick);
      // Toggling hobbyist/projector mode recreates the whole map instance;
      // this effect (keyed only on [mapReady]) doesn't tear down until the
      // *next* render once mapReady flips, so a queued frame from the old
      // map can still fire in between — bail out once `map` is no longer
      // the live one rather than crashing deep inside MapLibre internals.
      if (mapRef.current !== map) return;
      if (t - lastPlanes < 150) return;
      lastPlanes = t;
      const now = Date.now();
      const s = useStore.getState();
      const list = visibleRef.current;
      updatePlanes(map, list, s.selectedIcao24, now, s.layers.threeD, s.watchlist, s.layers.trueScale);
      updateSatellites(map, now, s.layers.showSatellites);
      if (s.layers.hobbyist) updateGlideSlope3D(map, approachesRef.current, s.layers.threeD);
      // Predicted lines follow the (moving) plane, so redraw each tick while on;
      // clearing when off is handled once in the routes/predicted effect below.
      if (s.layers.predicted) updatePredicted(map, list, routesRef.current, now);

      const sel = s.selectedIcao24 ? s.aircraft.get(s.selectedIcao24) : undefined;
      updateRoute(map, sel, s.selectedEnrichment, now);

      if (s.followSelected && sel) {
        // On phones the bottom sheet covers part of the map: center the plane
        // in the strip above it rather than behind it.
        if (isPhone()) {
          map.jumpTo({ center: projectPosition(sel, now, map), padding: { top: 0, right: 0, left: 0, bottom: s.sheetInset } });
        } else {
          map.setCenter(projectPosition(sel, now, map));
        }
      }

      // Keep animating the fade-out every tick while something is actively
      // fading; otherwise trails only redraw on real data changes below.
      if (fading.size > 0) {
        updateTrails(map, list, now, trailMaxAge(s.trails), fadingList());
      }
    };
    raf = requestAnimationFrame(tick);

    const redrawTrails = () => {
      const list = visibleRef.current;
      trackDisappearances(list);
      updateTrails(
        map,
        list,
        Date.now(),
        trailMaxAge(useStore.getState().trails),
        fadingList(),
      );
    };
    const redrawRings = () => {
      const s = useStore.getState();
      const center: [number, number] | null =
        s.layers.rings && s.area
          ? [(s.area.lomin + s.area.lomax) / 2, (s.area.lamin + s.area.lamax) / 2]
          : null;
      updateRings(map, center);
    };
    // Observed flight-lane density: only touch the map source when the
    // overlay is on (an empty grid clears it when off), independent of the
    // accumulator, which keeps running in the background regardless.
    const redrawLanes = () => {
      const s = useStore.getState();
      updateFlightLanes(map, s.layers.lanes ? laneGridRef.current : null);
    };

    // Phone: if the tapped plane would sit under the bottom sheet (or the top
    // bar), pan it into the visible strip between them.
    const keepSelectedAboveSheet = (inset: number) => {
      const s = useStore.getState();
      const sel = s.selectedIcao24 ? s.aircraft.get(s.selectedIcao24) : undefined;
      if (!sel || s.followSelected) return;
      const h = map.getContainer().clientHeight;
      const top = 120;
      const bottom = h - inset - 24;
      const y = map.project([sel.lon, sel.lat]).y;
      if (y < top || y > bottom) map.panBy([0, y - (top + bottom) / 2], { duration: 450 });
    };

    const unsub = useStore.subscribe((s, prev) => {
      // Same stale-map race as tick() above — this subscription lives for
      // the lifetime of the React effect, not the map instance.
      if (mapRef.current !== map) return;
      if (s.aircraft !== prev.aircraft || s.filters !== prev.filters) recomputeVisible();
      if (
        s.aircraft !== prev.aircraft ||
        s.filters !== prev.filters ||
        s.trails !== prev.trails
      )
        redrawTrails();
      if (s.aircraft !== prev.aircraft) accumulate(laneGridRef.current, s.aircraft.values());
      if (s.airports !== prev.airports) updateAirports(map, s.airports);
      if (s.layers.rings !== prev.layers.rings || s.area !== prev.area) redrawRings();
      if (s.layers.lanes !== prev.layers.lanes) redrawLanes();
      if (s.sheetInset !== prev.sheetInset && s.sheetInset > 0) keepSelectedAboveSheet(s.sheetInset);
      if (s.sheetInset === 0 && prev.sheetInset > 0) map.setPadding({ top: 0, right: 0, bottom: 0, left: 0 });
      if (s.focusTrack !== prev.focusTrack || (s.focusTrack?.live && s.aircraft !== prev.aircraft)) {
        const tail = s.selectedIcao24 ? s.aircraft.get(s.selectedIcao24) : undefined;
        updateFocusTrack(map, s.focusTrack, tail);
        // A past flight picked from history: frame it.
        const b = s.focusTrack?.flight.bbox;
        if (s.focusTrack && !s.focusTrack.live && s.focusTrack !== prev.focusTrack && b) {
          map.fitBounds([[b.lomin, b.lamin], [b.lomax, b.lamax]], { padding: 80, maxZoom: 9, duration: 700 });
        }
      }
    });
    recomputeVisible();
    redrawTrails();
    updateAirports(map, useStore.getState().airports);
    redrawRings();
    redrawLanes();
    updateFocusTrack(map, useStore.getState().focusTrack);

    // The lanes heatmap doesn't need to redraw on every aircraft tick (every
    // few seconds is plenty smooth and avoids needless source churn); prune
    // expired cells on the same cadence so the grid's memory stays bounded.
    const lanesIv = setInterval(() => {
      if (mapRef.current !== map) return;
      prune(laneGridRef.current);
      redrawLanes();
    }, 5000);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(lanesIv);
      unsub();
    };
  }, [mapReady]);

  // 3D view: tilt the camera so altitude-lifted aircraft read as elevated.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    map.easeTo({ pitch: threeD ? 60 : 0, duration: 600 });
  }, [mapReady, threeD]);

  // Radar coverage rings: static geometry, redrawn only on toggle/altitude
  // change (not per tick — see updateRadarCoverage's doc comment).
  const showRadar = useStore((s) => s.layers.showRadar);
  const radarAltitudeFt = useStore((s) => s.radarAltitudeFt);
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    updateRadarCoverage(map, radarAltitudeFt, showRadar);
  }, [mapReady, showRadar, radarAltitudeFt]);

  // Satellites: fetch TLEs once, the first time the overlay is ever enabled
  // (updateSatellites itself runs every tick above, gated on `visible`).
  const showSatellites = useStore((s) => s.layers.showSatellites);
  const tlesLoadedRef = useRef(false);
  useEffect(() => {
    if (!showSatellites || tlesLoadedRef.current) return;
    tlesLoadedRef.current = true;
    void loadTles().catch((err) => console.error('TLE load failed:', err));
  }, [showSatellites]);

  // Precipitation radar (weather.ts module): refresh on enable + every 5 min.
  const showPrecip = useStore((s) => s.layers.showPrecip);
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    setPrecipVisible(map, showPrecip);
    if (!showPrecip) return;
    void refreshPrecip(map);
    const iv = setInterval(() => void refreshPrecip(map), 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [mapReady, showPrecip]);

  // METARs: refresh on enable + whenever the subscribed bbox changes + every 10 min.
  const showMetars = useStore((s) => s.layers.showMetars);
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    setMetarsVisible(map, showMetars);
    if (!showMetars || !liveBbox) return;
    void refreshMetars(map, liveBbox);
    const iv = setInterval(() => void refreshMetars(map, liveBbox), 10 * 60 * 1000);
    return () => clearInterval(iv);
  }, [mapReady, showMetars, liveBbox]);

  // Full origin→destination route lines for every aircraft (opt-in), and/or the
  // predicted remaining-leg lines (current position -> destination) drawn every
  // tick above. Both consume the same bulk route-endpoint lookup, polled every
  // 15 s; lines fill in as the server-side route cache warms.
  const routesOn = useStore((s) => s.layers.routes);
  const predictedOn = useStore((s) => s.layers.predicted);
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    if (!routesOn) updateAllRoutes(map, {});
    if (!predictedOn) updatePredicted(map, [], {}, Date.now()); // clear once; the tick skips drawing while off
    if (!routesOn && !predictedOn) {
      routesRef.current = {};
      return;
    }
    let cancelled = false;
    const poll = async () => {
      const items = [...useStore.getState().aircraft.values()]
        .filter((ac) => ac.callsign)
        .map((ac) => ({ icao24: ac.icao24, callsign: ac.callsign }));
      if (items.length === 0) return;
      try {
        const res = await fetch(apiUrl('/api/routes'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) return;
        const data = (await res.json()) as { routes: Record<string, RouteEndpoints> };
        if (cancelled || !mapRef.current) return;
        routesRef.current = data.routes ?? {};
        if (routesOn) updateAllRoutes(map, routesRef.current);
      } catch {
        /* transient — retry on next tick */
      }
    };
    void poll();
    const iv = setInterval(() => void poll(), 15_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
      routesRef.current = {};
      if (routesOn) updateAllRoutes(map, {});
    };
  }, [mapReady, routesOn, predictedOn]);

  if (!bbox) {
    return (
      <main className="map-error">
        <h1>No area to track</h1>
        <p>This link is missing a valid map area. Pick one to start tracking live traffic.</p>
        <button className="chip active" onClick={() => navigate('/')}>
          <ArrowLeft size={15} weight="bold" /> Pick an area
        </button>
      </main>
    );
  }

  return (
    <div className={`map-view${projector ? ' projector' : ''}`}>
      <div ref={mapContainer} className="map-container" />
      <ToastStack />
      {!projector && (
        <>
          <header className="command-bar glass">
            <div className="bar-group bar-identity">
              <button className="icon-btn" onClick={() => navigate('/')} title="Back to areas" aria-label="Back to areas">
                <ArrowLeft size={17} weight="bold" />
              </button>
              <div className="bar-title">
                <span className="bar-kicker">Tracking</span>
                <span className="bar-area">{areaName ?? 'Live traffic'}</span>
              </div>
            </div>
            <SearchBox />
            <span className="bar-sep sep-tools" aria-hidden="true" />
            <div className="bar-group bar-tools">
              <FilterPanel />
              <MapToolbar />
              <WatchlistPanel />
            </div>
            <span className="bar-sep sep-view" aria-hidden="true" />
            <div className="bar-group bar-view">
              <button
                className={`icon-btn${locateMe.locating ? ' is-busy' : ''}`}
                onClick={locateMe.locate}
                disabled={locateMe.locating}
                title="Show traffic around my location"
                aria-label="Show traffic around my location"
              >
                <NavigationArrow size={17} weight="fill" />
              </button>
              <button
                className={`icon-btn${isFullscreen ? ' active' : ''}`}
                onClick={toggleFullscreen}
                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                aria-pressed={isFullscreen}
              >
                {isFullscreen ? <CornersIn size={17} weight="bold" /> : <CornersOut size={17} weight="bold" />}
              </button>
              <button
                className="icon-btn"
                onClick={toggleProjector}
                title="Projector mode: clock and traffic only, any key exits"
                aria-label="Projector mode"
              >
                <Television size={17} weight="bold" />
              </button>
            </div>
          </header>
          {locateMe.error && locateMe.errorKind && (
            <div className="location-help-overlay glass">
              <LocationHelp
                kind={locateMe.errorKind}
                message={locateMe.error}
                permission={locateMe.errorPermission}
                detail={locateMe.errorDetail}
                onRetry={locateMe.locate}
                onDismiss={locateMe.clearError}
              />
            </div>
          )}
          <FlightInfoPanel />
          <AirportPanel />
          <AircraftList />
          <AltitudeLegend />
          <StatusBar />
          {phone && !selectedIcao24 && <InstallPrompt variant="banner" />}
          {drawingCircle && (
            <div className="map-notice draw-hint-chip glass is-hint" role="status">
              <span className="notice-icon">
                <CircleDashed size={17} weight="bold" />
              </span>
              <span>
                <strong>Draw the coverage area</strong>
                Click and drag on the map. <kbd>Esc</kbd> cancels.
              </span>
            </div>
          )}
        </>
      )}
      {projector && <ProjectorOverlay areaName={areaName} onExit={toggleProjector} />}
      {!projector && isWaiting && (
        <div className="map-notice empty-notice glass" role="status">
          <span className="notice-icon">
            <span className="notice-spinner" aria-hidden="true" />
          </span>
          <span>
            <strong>Connecting to live feeds</strong>
            {networkHint === 'prompt'
              ? 'Your browser is asking whether PlaneSight may connect to devices on your network (your Tailscale server). Choose Allow in the prompt by the address bar.'
              : networkHint === 'denied'
                ? 'Your browser is blocking PlaneSight from reaching your server. Click the icon left of the address bar → Site settings → Local network access → Allow, then reload.'
                : 'Aircraft appear as the first positions arrive.'}
          </span>
        </div>
      )}
      {isEmpty && (
        <div className={`map-notice empty-notice glass${allSourcesDown ? ' is-error' : ''}`} role="status">
          <span className="notice-icon">
            {allSourcesDown ? <WifiSlash size={17} weight="bold" /> : <AirplaneTilt size={17} weight="bold" />}
          </span>
          {allSourcesDown ? (
            <span>
              <strong>All data sources are unreachable</strong>
              Retrying automatically.
            </span>
          ) : (
            <span>
              <strong>No aircraft in this area right now</strong>
              Zoom out or pick a busier region.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
